import { BlockType, effectiveDisabledIds, type Block } from '../../types';

/** Repair only the compiler-owned, single-consumer legacy exit bridge. Generic
 * expression operands and hand-authored thresholds are never reinterpreted.
 * Persisted provenance stays intact; the config records the execution repair.
 */
export function healStructuralExitBridges(blocks: Block[]): Block[] {
  const byId = new Map(blocks.map(b => [b.id, b]));
  if (byId.size !== blocks.length) return blocks;
  const disabled = effectiveDisabledIds(blocks);
  const updates = new Map<string, Block>();
  const removed = new Set<string>();
  for (const config of blocks.filter(b => b.type === BlockType.STRATEGY_CONFIG && typeof b.data.setupSlug === 'string')) {
    const slug = String(config.data.setupSlug);
    if (byId.get(`${slug}-machine`)?.type !== BlockType.SETUP_STATE) continue;
    let repaired = false;
    for (const bridge of blocks.filter(b => b.type === BlockType.EXPRESSION && b.id.startsWith(`${slug}-`))) {
      const suffix = bridge.id.slice(slug.length);
      const match = suffix.match(/^-(inval|target)-expr-(\d+)$/);
      if (!match) continue;
      const [, kind, index] = match;
      const eventId = `${slug}-${kind === 'inval' ? 'inval-exit' : 'target'}-ev-${index}`;
      const exitId = `${slug}-${kind}-exit-${index}`;
      const event = updates.get(eventId) ?? byId.get(eventId), exit = byId.get(exitId);
      // Legacy serialized data predates the current operand union.
      const d = bridge.data as unknown as Record<string, unknown>;
      if ([config.id, `${slug}-machine`, bridge.id, eventId, exitId].some(id => disabled.has(id))) continue;
      if (!event || event.type !== BlockType.SETUP_EVENT || !exit || exit.type !== BlockType.EXIT ||
          d.category !== 'CONDITION' || d.leftSource !== 'input' || d.leftInputBlockId !== eventId ||
          d.operator !== '>' || d.rightSource !== 'value' || d.rightValue !== 0 || d.invert || d.persistence ||
          Number(d.leftShift ?? 0) !== 0 || Number(d.rightShift ?? 0) !== 0 ||
          bridge.connections?.inputs?.length !== 1 || bridge.connections.inputs[0] !== eventId ||
          bridge.connections?.outputs?.length !== 1 || bridge.connections.outputs[0] !== exitId ||
          exit.connections?.inputs?.length !== 1 || exit.connections.inputs[0] !== bridge.id ||
          !event.connections?.outputs?.includes(bridge.id) ||
          (kind === 'target' ? event.data.setupStage !== 'exit' : (event.data.parameters as Record<string, unknown>)?.role !== 'exit_trigger')) continue;
      // Refuse shared or user-extended bridges rather than deleting their signal.
      if (blocks.some(b => b.id !== exitId && ((b.connections?.inputs ?? []).includes(bridge.id) ||
          Object.entries(b.data).some(([key, value]) => key.endsWith('BlockId') && value === bridge.id)))) continue;
      removed.add(bridge.id);
      updates.set(eventId, { ...event, connections: { ...event.connections, outputs: event.connections.outputs.map(id => id === bridge.id ? exitId : id) } });
      updates.set(exitId, { ...exit, connections: { ...exit.connections, inputs: [eventId] } });
      repaired = true;
    }
    if (repaired) updates.set(config.id, { ...config, data: { ...config.data, structuralExitRepair: 'direct-event-signals@1' } });
  }
  return removed.size ? blocks.filter(b => !removed.has(b.id)).map(b => updates.get(b.id) ?? b) : blocks;
}
